import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, Image as ImageIcon, Copy, Check, Trash2, Loader2 } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';

import { API_BASE } from '../lib/api';
import { usePermissions } from '../hooks/usePermissions';
import { PERMISSIONS } from '../lib/permissions';
import { smartCompressImage, CompressResult } from '../lib/imageCompression';

interface MediaItem {
  id: number | string;
  key: string;
  name: string;
  alt: string;
  folder: string;
  tags: string[];
  contentType: string;
  size: number;
  uploaded: string;
  url: string;
}

interface Product {
  id: number;
  name: string;
  image_url: string | null;
}

const formatSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

interface FileCompressStat {
  name: string;
  isCompressed: boolean;
  originalSize: number;
  compressedSize: number;
  ratio: string;
}

interface AdminUser {
  id: number;
  role: string;
  supplier_tag?: string;
  supplier_label?: string;
}

const AdminMediaLibrary: React.FC = () => {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const canWriteMedia = can(PERMISSIONS.MEDIA_WRITE);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [currentUser] = useState<AdminUser | null>(() => {
    try {
      const raw = localStorage.getItem('admin_user');
      return raw ? (JSON.parse(raw) as AdminUser) : null;
    } catch {
      return null;
    }
  });
  const isSupplier = currentUser?.role === 'supplier';
  const supplierTag = currentUser?.supplier_tag || '';
  const supplierLabel = currentUser?.supplier_label || '';

  const [mediaList, setMediaList] = useState<MediaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [, setCompressStats] = useState<FileCompressStat[]>([]);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'all' | 'web' | 'food' | 'fungtai' | 'other'>('all');
  const [uploadFolder, setUploadFolder] = useState(isSupplier ? supplierTag : 'web');
  const [products, setProducts] = useState<Product[]>([]);

  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchMedia();
    fetchProducts();
  }, [token, navigate]);

  const fetchProducts = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/public/admin/products`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setProducts(await res.json());
      } else if (res.status === 401) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        navigate('/admin');
      }
    } catch (e) {
      console.error('Failed to fetch products:', e);
    }
  };

  const fetchMedia = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}/api/public/admin/media`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setMediaList(data);
      } else if (res.status === 401) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        navigate('/admin');
      } else {
        setError('獲取媒體列表失敗');
      }
    } catch (e) {
      setError('獲取媒體列表失敗');
    } finally {
      setLoading(false);
    }
  };

  const uploadSingleFile = async (file: File): Promise<{ ok: boolean; stat: FileCompressStat }> => {
    let result: CompressResult;
    try {
      result = await smartCompressImage(file);
    } catch {
      result = {
        isCompressed: false,
        file,
        originalSize: file.size,
        compressedSize: file.size,
        ratio: '0%',
      };
    }

    const formData = new FormData();
    formData.append('file', result.file);
    if (!isSupplier) {
      formData.append('folder', uploadFolder);
    }

    try {
      const res = await fetch(`${API_BASE}/api/public/admin/media/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (res.status === 401) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        navigate('/admin');
        return { ok: false, stat: { name: file.name, ...result } };
      }
      return { ok: res.ok, stat: { name: file.name, ...result } };
    } catch (e) {
      return { ok: false, stat: { name: file.name, ...result } };
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    const invalidFiles = files.filter(f => !allowedTypes.includes(f.type));
    if (invalidFiles.length > 0) {
      setError(`請選擇有效的圖片檔案（JPG、PNG、GIF、WEBP、SVG）。以下檔案不支援：${invalidFiles.map(f => f.name).join(', ')}`);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setError('');
    setSuccess('');

    let successCount = 0;
    let failCount = 0;
    const stats: FileCompressStat[] = [];

    for (let i = 0; i < files.length; i++) {
      setUploadProgress(Math.round((i / files.length) * 100));
      const { ok, stat } = await uploadSingleFile(files[i]);
      stats.push(stat);
      if (ok) successCount++;
      else failCount++;
    }

    setCompressStats(stats);
    setUploadProgress(100);
    await fetchMedia();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setTimeout(() => {
      setUploading(false);
      setUploadProgress(0);
      if (failCount > 0) {
        setError(`上傳完成：${successCount} 個成功，${failCount} 個失敗`);
      } else if (successCount > 0) {
        const compressedCount = stats.filter(s => s.isCompressed).length;
        const totalSaved = stats.reduce((sum, s) => sum + (s.originalSize - s.compressedSize), 0);
        setSuccess(
          `上傳完成：${successCount} 個成功` +
          (compressedCount > 0 ? `，${compressedCount} 個已壓縮，共節省 ${formatSize(totalSaved)}` : '')
        );
      }
    }, 500);
  };

  const handleCopyUrl = async (item: MediaItem) => {
    try {
      await navigator.clipboard.writeText(item.url);
      setCopiedKey(item.key);
      setTimeout(() => setCopiedKey(null), 2000);
    } catch {
      setError('複製 URL 失敗');
    }
  };

  const handleDelete = async (item: MediaItem) => {
    if (!confirm(`確定要刪除「${item.name}」嗎？此操作無法復原。`)) return;

    try {
      const res = await fetch(`${API_BASE}/api/public/admin/media/${item.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setMediaList(prev => prev.filter(m => m.key !== item.key));
      } else if (res.status === 401) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        navigate('/admin');
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.error || '刪除失敗');
      }
    } catch (e) {
      setError('刪除失敗');
    }
  };

  const productMedia = products
    .filter(p => p.image_url && !mediaList.some(m => m.url === p.image_url))
    .map(p => ({
      id: `product-${p.id}`,
      key: p.image_url!,
      name: p.name,
      alt: p.name,
      folder: 'food',
      tags: [],
      contentType: 'image/*',
      size: 0,
      uploaded: '',
      url: p.image_url!,
    }));

  const allItems = [...mediaList, ...productMedia];
  const webFolders = ['web', 'story', 'general'];
  const filteredItems = allItems.filter(item => {
    if (activeTab === 'all') return true;
    const folder = item.folder || 'general';
    if (activeTab === 'web') return webFolders.includes(folder);
    if (activeTab === 'food') return folder === 'food';
    if (activeTab === 'fungtai') return folder === 'fungtai';
    return !webFolders.includes(folder) && folder !== 'food' && folder !== 'fungtai';
  });

  const tabs: { key: typeof activeTab; label: string }[] = [
    { key: 'all', label: `全部 (${allItems.length})` },
    { key: 'web', label: '網頁圖片' },
    { key: 'food', label: '食品圖片' },
    { key: 'fungtai', label: '豐泰' },
    { key: 'other', label: '其他' },
  ];

  return (
    <AdminLayout currentPage="media-library">
      <div>
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex justify-between items-center">
            <span>{error}</span>
            <button onClick={() => setError('')} className="text-red-500 hover:text-red-700">✕</button>
          </div>
        )}

        {success && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 flex justify-between items-center">
            <span>{success}</span>
            <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700">✕</button>
          </div>
        )}

        {/* Upload Section */}
        {canWriteMedia && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
            <Upload size={20} className="text-blue-600" />
            上傳圖片
          </h2>

          {!isSupplier && (
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-4">
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600 dark:text-gray-300">分類：</label>
                <select
                  value={uploadFolder}
                  onChange={e => setUploadFolder(e.target.value)}
                  className="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-800 text-gray-800 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="web">網頁圖片</option>
                  <option value="food">食品圖片</option>
                  <option value="fungtai">豐泰</option>
                  <option value="general">其他</option>
                </select>
              </div>
            </div>
          )}

          <div className="flex items-center gap-4">
            <label className="flex-1 cursor-pointer">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileChange}
                disabled={uploading}
                className="hidden"
              />
              <div className="flex items-center gap-3 px-4 py-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-blue-400 dark:hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors">
                <ImageIcon size={24} className="text-gray-400 dark:text-gray-500" />
                <span className="text-sm text-gray-600 dark:text-gray-300">
                  {uploading ? '正在上傳...' : '點擊選擇一個或多個圖片檔案'}
                </span>
              </div>
            </label>
          </div>

          {uploading && (
            <div className="mt-4">
              <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-300 mb-1">
                <span className="flex items-center gap-2">
                  <Loader2 size={16} className="animate-spin" />
                  上傳中...
                </span>
                <span>{uploadProgress}%</span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                <div
                  className="bg-blue-600 h-2.5 rounded-full transition-all duration-200"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          )}
        </div>
        )}

        {/* Category Tabs / Supplier Heading */}
        {isSupplier ? (
          <div className="mb-4">
            <h2 className="text-xl font-bold text-gray-800 dark:text-white">
              {supplierLabel ? `${supplierLabel}媒體庫` : '媒體庫'}
            </h2>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2 mb-4">
            {tabs.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  activeTab === tab.key
                    ? 'bg-blue-600 text-white'
                    : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}

        {/* Media Grid */}
        {loading ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center text-gray-500 dark:text-gray-400">
            <Loader2 size={24} className="animate-spin mx-auto mb-2" />
            加載中...
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center text-gray-500 dark:text-gray-400">
            <ImageIcon size={48} className="mx-auto mb-3 text-gray-300 dark:text-gray-600" />
            <p>此分類尚未上傳任何圖片</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {filteredItems.map(item => (
              <div
                key={item.id}
                className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden hover:shadow-md transition-shadow"
              >
                {/* Thumbnail */}
                <div className="aspect-square bg-gray-50 dark:bg-gray-900 flex items-center justify-center overflow-hidden">
                  <img
                    src={item.url}
                    alt={item.name}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    onError={e => {
                      (e.target as HTMLImageElement).src = '';
                      (e.target as HTMLImageElement).style.display = 'none';
                      const parent = (e.target as HTMLImageElement).parentElement;
                      if (parent) {
                        const fallback = document.createElement('div');
                        fallback.className = 'flex flex-col items-center justify-center text-gray-400 dark:text-gray-500';
                        fallback.innerHTML = `<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg><span class="text-xs mt-1">無法顯示</span>`;
                        parent.appendChild(fallback);
                      }
                    }}
                  />
                </div>

                {/* Info */}
                <div className="p-3">
                  <p className="text-sm font-medium text-gray-800 dark:text-white truncate" title={item.name}>
                    {item.name}
                  </p>
                  {item.alt && item.alt !== item.name && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate" title={item.alt}>
                      {item.alt}
                    </p>
                  )}
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded">
                      {item.folder || 'default'}
                    </span>
                    {typeof item.id === 'string' && String(item.id).startsWith('product-') ? (
                      <p className="text-xs text-gray-500 dark:text-gray-400">產品圖片</p>
                    ) : (
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {formatSize(item.size)} · {new Date(item.uploaded).toLocaleDateString('zh-HK')}
                      </p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={() => handleCopyUrl(item)}
                      className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 rounded hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
                    >
                      {copiedKey === item.key ? (
                        <>
                          <Check size={14} />
                          已複製
                        </>
                      ) : (
                        <>
                          <Copy size={14} />
                          複製 URL
                        </>
                      )}
                    </button>
                    {canWriteMedia && (
                      <button
                        onClick={() => {
                          if (typeof item.id === 'string' && item.id.startsWith('product-')) return;
                          handleDelete(item);
                        }}
                        disabled={typeof item.id === 'string' && item.id.startsWith('product-')}
                        className={`flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium rounded ${
                          typeof item.id === 'string' && item.id.startsWith('product-')
                            ? 'text-gray-400 bg-gray-100 cursor-not-allowed'
                            : 'text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30'
                        }`}
                      >
                        <Trash2 size={14} />
                        刪除
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminMediaLibrary;
